import React, { useState, Fragment, useCallback, useEffect } from 'react';
import _ from 'lodash';
import { AutoSizer } from 'react-virtualized';
import { connect } from 'react-redux';
import { Loading } from 'components/Loading';
import { updateOrderBookSelection } from 'redux/actions/exchange';
import { withNamespaces } from 'react-i18next';
import { Box } from 'components/Wrapped';
import { getSingleTradingPairSettings } from 'redux/selectors/settings';
import { getRecentTradeHistoryItem } from 'redux/selectors/exchange';
import { DemandPercentLine } from './components/DemandPercentLine';
import { OrderBookModeSelector } from './components/OrderBookModeSelector';
import { OrderBookLastPrice } from './components/OrderBookLastPrice';
import { OrderTable, OrderTableHead } from './components/OrderTable';

const OrderBook = (props) => {
  const {
    tradingPair,
    lastPrice,
    tradingPairSettings: {
      tickDecimals,
      tradeAmountDecimals,
      // orderValueDecimals,
    },
    tradeHistoryItem: { side },
    orderBookAsk,
    orderBookBid,
    updateOrderBookSelection,
    maxVolumeAsk,
    maxVolumeBid,
    t,
  } = props;
  const { baseCurrency, quoteCurrency } = tradingPair;
  const [orderMode, setOrderMode] = useState('multi'); // 'multi' | 'sell' | 'buy'
  const [orderBookVisibleAsk, setOrderBookVisibleAsk] = useState([]);
  const [askVisibleVolume, setAskVisibleVolume] = useState(0);
  const [orderBookVisibleBid, setOrderBookVisibleBid] = useState([]);
  const [bidVisibleVolume, setBidVisibleVolume] = useState(0);

  useEffect(() => {
    const visibleAsk = orderBookAsk.slice(orderMode === 'sell' ? -20 : orderMode === 'multi' ? -10 : 0);
    const askVolume = Math.round(orderBookAsk.reduce((acc, item) => acc + item.volume, 0));
    const visibleBid = orderBookBid.slice(0, orderMode === 'buy' ? 20 : orderMode === 'multi' ? 10 : 0);
    const bidVolume = Math.round(orderBookBid.reduce((acc, item) => acc + item.volume, 0));

    setOrderBookVisibleAsk(visibleAsk);
    setAskVisibleVolume(askVolume);
    setOrderBookVisibleBid(visibleBid);
    setBidVisibleVolume(bidVolume);
  }, [orderBookAsk, orderBookBid, orderMode]);

  const handleRowClick = useCallback(
    (side, rowData, index) => {
      if (!_.isEmpty(rowData)) {
        updateOrderBookSelection({
          rowData,
          side,
          index,
          updatedOrderSide: side,
        });
      }
    },
    [updateOrderBookSelection],
  );

  const onChangeOrderMode = useCallback((mode) => {
    setOrderMode(mode);
  }, []);

  return (
    <Fragment>
      <OrderBookModeSelector orderMode={orderMode} onChangeOrderMode={onChangeOrderMode} />
      <div className="react-virtualized-container-fix">
        {!_.isEmpty(tradingPair) ? (
          <AutoSizer>
            {({ height, width }) => (
              <Box pad="none">
                {/* Table header */}
                <OrderTableHead t={t} baseCurrency={baseCurrency} quoteCurrency={quoteCurrency} width={width} />
                {/* Table ask */}
                {['multi', 'sell'].includes(orderMode) && (
                  <OrderTable
                    tableData={orderBookVisibleAsk}
                    side="ask"
                    height={'multi' === orderMode ? height / 2 - 32 : height - 65}
                    width={width}
                    maxVolumeAsk={maxVolumeAsk}
                    tradeAmountDecimals={tradeAmountDecimals}
                    tickDecimals={tickDecimals}
                    handleRowClick={handleRowClick}
                  />
                )}
                {!_.isEmpty(tradingPair) && (
                  <OrderBookLastPrice width={width} side={side} lastPrice={lastPrice} tickDecimals={tickDecimals} />
                )}
                {/* Table bid */}
                {['multi', 'buy'].includes(orderMode) && (
                  <OrderTable
                    tableData={orderBookVisibleBid}
                    side="bid"
                    height={'multi' === orderMode ? height / 2 - 32 : height - 65}
                    width={width}
                    maxVolumeAsk={maxVolumeBid}
                    tradeAmountDecimals={tradeAmountDecimals}
                    tickDecimals={tickDecimals}
                    handleRowClick={handleRowClick}
                  />
                )}
              </Box>
            )}
          </AutoSizer>
        ) : (
          <Box pad="none" flex={true}>
            <Loading />
          </Box>
        )}
      </div>
      <DemandPercentLine
        buyPercentValue={Math.round((bidVisibleVolume / (askVisibleVolume + bidVisibleVolume)) * 100)}
        sellPercentValue={Math.round((askVisibleVolume / (askVisibleVolume + bidVisibleVolume)) * 100)}
      />
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  tradingPairSettings: getSingleTradingPairSettings(state, {
    tradingPair: state.exchange.tradingPair,
  }),
  tradeHistoryItem: getRecentTradeHistoryItem(state),
  orderBookAsk: state.exchange.orderBookAsk,
  orderBookBid: state.exchange.orderBookBid,
  tradingPair: state.exchange.tradingPair,
  lastPrice: state.exchange.tradingPairStats.lastPrice,
  tradingPairStats: state.exchange.tradingPairStats,
  maxVolumeAsk: state.exchange.orderBookAskMaxVolume,
  maxVolumeBid: state.exchange.orderBookBidMaxVolume,
  currencySettings: state.exchangeSettings.currencySettings,
});

export default withNamespaces()(connect(mapStateToProps, { updateOrderBookSelection })(OrderBook));
